import { Children, useId, useState } from 'react'
import { useLanguage } from '../contexts/LanguageContext'
import { buildLocalizedPath } from '../lib/localeSeoRoutes'
import { G2_PRIME_DOCUMENT_SIGN_URL, TRUSTPILOT_PRIME_DOCUMENT_SIGN_URL } from '../lib/trustListingUrls'
import { PublicPictureImg } from './PublicPictureImg'
import { TrustpilotReviewButton } from './trust/TrustpilotReviewButton'
import { BrandSocialIconLinks } from './BrandSocialIconLinks'

/** Shown under Resources in the footer; full template hub, AU page, and blog stay in header nav only. */
const FOOTER_RESOURCES_PRIMARY = [
  ['/esignature', 'footer.linkEsignature'],
  ['/how-to-sign-documents-online', 'footer.linkHowSignOnline'],
  ['/how-it-works', 'footer.linkHowItWorksPage'],
  ['/esignature-for-freelancers', 'footer.linkEsignFreelancers'],
  ['/free-contract-templates', 'footer.linkFreeContractTemplates'],
  ['/secure-esignature', 'footer.linkSecureEsign'],
  ['/esignature/real-estate', 'footer.linkIndustryRealEstate'],
  ['/esignature/accountants', 'footer.linkIndustryAccountants'],
  ['/esignature/hr', 'footer.linkIndustryHr'],
  ['/esignature/agencies', 'footer.linkIndustryAgencies'],
  ['/free-esignature', 'footer.linkFreeEsign'],
]

const FOOTER_LINKS_INITIAL = 4

function FooterExpandableColumn({ title, expanded, onToggle, children }) {
  const { t } = useLanguage()
  const panelId = useId()
  const items = Children.toArray(children).filter((child) => child != null && child !== false)

  if (items.length <= FOOTER_LINKS_INITIAL) {
    return (
      <div className="footer-links-col">
        <h5>{title}</h5>
        {items}
      </div>
    )
  }

  const first = items.slice(0, FOOTER_LINKS_INITIAL)
  const rest = items.slice(FOOTER_LINKS_INITIAL)

  return (
    <div className="footer-links-col">
      <h5>{title}</h5>
      {first}
      <div id={panelId} className="footer-links-col__panel" hidden={!expanded}>
        {rest}
      </div>
      <button
        type="button"
        className="footer-links-col__toggle"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={onToggle}
      >
        {expanded ? t('footer.showLess') : t('footer.showMore')}
      </button>
    </div>
  )
}

/**
 * Shared Footer component used by:
 * - Landing page (auth, blog views)
 * - App view (document signing, email activity)
 * Ensures identical footer content and behavior across the site.
 */
export function Footer({ onNavigate, termsHref, privacyHref, onTermsClick, onPrivacyClick }) {
  const { t, language } = useLanguage()
  const [footerColExpanded, setFooterColExpanded] = useState({
    product: false,
    resources: false,
    company: false,
  })
  const lp = (logicalPath) => buildLocalizedPath(logicalPath, language)
  const scrollTo = (id) => {
    if (onNavigate) {
      onNavigate('auth')
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 100)
    }
  }

  return (
    <footer className="site-footer">
      <div className="footer-top">
        {/* Brand column */}
        <div className="footer-brand">
          <PublicPictureImg
            name="primedocumentsignlogo"
            alt="Prime Document Sign"
            className="footer-brand-logo-full"
            width={89}
            height={38}
            decoding="async"
            loading="lazy"
            fetchPriority="low"
          />
          <p className="footer-brand-tagline">{t('footer.tagline')}</p>
          <div className="footer-trust-chips">
            <span className="footer-trust-chip">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              AES-256 Encrypted
            </span>
            <span className="footer-trust-chip">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polyline points="20 6 9 17 4 12"/></svg>
              Legally Binding
            </span>
          </div>
          <div className="footer-trustpilot-cta">
            <TrustpilotReviewButton className="trustpilot-review-btn--footer" />
          </div>
        </div>

        {/* Nav columns */}
        <div className="footer-links">
          <FooterExpandableColumn
            title={t('footer.product')}
            expanded={footerColExpanded.product}
            onToggle={() =>
              setFooterColExpanded((prev) => ({ ...prev, product: !prev.product }))
            }
          >
            <button type="button" className="footer-link" onClick={() => scrollTo('how')}>
              {t('nav.howItWorks')}
            </button>
            <button type="button" className="footer-link" onClick={() => scrollTo('how')}>
              {t('footer.templates')}
            </button>
            <button type="button" className="footer-link" onClick={() => scrollTo('pricing')}>
              {t('nav.pricing')}
            </button>
            <a href={lp('/docusign-alternative')} className="footer-link">
              {t('footer.docusignAlt')}
            </a>
            <a href={lp('/docusign-vs-prime-document-sign')} className="footer-link">
              {t('footer.linkDocuSignVs')}
            </a>
            <a href={lp('/product')} className="footer-link">
              {t('footer.linkProduct')}
            </a>
            <a href={lp('/alternatives')} className="footer-link">
              {t('footer.linkAlternatives')}
            </a>
            <a href={lp('/what-is-esignature')} className="footer-link">
              {t('footer.linkWhatIsEsign')}
            </a>
          </FooterExpandableColumn>
          <FooterExpandableColumn
            title={t('footer.resources')}
            expanded={footerColExpanded.resources}
            onToggle={() =>
              setFooterColExpanded((prev) => ({ ...prev, resources: !prev.resources }))
            }
          >
            {FOOTER_RESOURCES_PRIMARY.map(([path, labelKey]) => (
              <a key={path} href={lp(path)} className="footer-link">
                {t(labelKey)}
              </a>
            ))}
          </FooterExpandableColumn>
          <FooterExpandableColumn
            title={t('footer.company')}
            expanded={footerColExpanded.company}
            onToggle={() =>
              setFooterColExpanded((prev) => ({ ...prev, company: !prev.company }))
            }
          >
            <a href={lp('/about')} className="footer-link">
              {t('nav.aboutUs')}
            </a>
            <a href={lp('/trust')} className="footer-link">
              {t('footer.linkTrust')}
            </a>
            <a href={lp('/reviews')} className="footer-link">
              {t('footer.linkReviews')}
            </a>
            <a href={lp('/why-primedocumentsign')} className="footer-link">
              {t('footer.linkWhyPrimeDocuSign')}
            </a>
            <a href={lp('/pricing')} className="footer-link">
              {t('footer.linkPricingPage')}
            </a>
            <a href={lp('/contact')} className="footer-link">
              {t('footer.linkContactPage')}
            </a>
            <a href={lp('/security')} className="footer-link">
              {t('footer.linkSecurity')}
            </a>
            <a href={lp('/esignature-legality')} className="footer-link">
              {t('footer.linkEsignLegality')}
            </a>
            <button type="button" className="footer-link" onClick={() => scrollTo('faq')}>
              {t('nav.faqs')}
            </button>
            <button type="button" className="footer-link" onClick={() => scrollTo('contact')}>
              {t('footer.contactSupport')}
            </button>
            <a href={G2_PRIME_DOCUMENT_SIGN_URL} target="_blank" rel="noopener noreferrer" className="footer-link">
              {t('footer.linkG2')}
            </a>
            <a href={TRUSTPILOT_PRIME_DOCUMENT_SIGN_URL} target="_blank" rel="noopener noreferrer" className="footer-link">
              {t('footer.linkTrustpilot')}
            </a>
          </FooterExpandableColumn>
          <div>
            <h5>Follow Us</h5>
            <BrandSocialIconLinks />
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <span>{t('footer.copyright')}</span>
        <div className="footer-legal">
          <a href={termsHref} className="footer-link" onClick={onTermsClick}>
            {t('footer.terms')}
          </a>
          <a href={privacyHref} className="footer-link" onClick={onPrivacyClick}>
            {t('footer.privacy')}
          </a>
        </div>
      </div>
    </footer>
  )
}
